import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import IconCollection from '../IconCollection/IconCollection';
import css from './SearchSuggestions.module.css';

/**
 * The search type-ahead dropdown (RAS-2536).
 *
 * Presentational: the data, debouncing and open/highlight state all come from
 * hooks/useSearchSuggestions. This file owns rendering, keyboard semantics and accessibility.
 */

// Must match MAX_TERM_WORDS in server/api-util/searchTerms.js. Enforced again here as a
// last-resort guard: the extraction side can never produce a term longer than this, so if one
// ever appears it means the stored data has regressed — and the one thing this feature must
// never do is show a customer a raw listing title.
export const MAX_SUGGESTION_WORDS = 4;

const isRenderableSuggestion = suggestion =>
  typeof suggestion?.value === 'string' &&
  suggestion.value.trim().length > 0 &&
  suggestion.value.trim().split(/\s+/).length <= MAX_SUGGESTION_WORDS;

/**
 * Split a suggestion into bold / plain runs against what the customer has typed.
 *
 * The mocks bold the TYPED portion and leave the completion plain — the inverse of the usual
 * autocomplete convention, and confirmed as deliberate.
 *
 * Returns plain data rather than markup on purpose: suggestion values originate in
 * seller-written listing text, so they are only ever rendered as text nodes. No
 * dangerouslySetInnerHTML anywhere in this path.
 *
 * @returns {Array<{text: string, bold: boolean}>}
 */
export const highlightSegments = (value, query) => {
  const text = String(value == null ? '' : value);
  const tokens = String(query == null ? '' : query)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    // Longest first, so "gen" never wins over "generator" on the same word.
    .sort((a, b) => b.length - a.length);

  if (!tokens.length) return [{ text, bold: false }];

  const segments = [];
  // Split on whitespace but keep the separators, so spacing survives reassembly.
  text.split(/(\s+)/).forEach(part => {
    if (!part) return;
    const lower = part.toLowerCase();
    const match = tokens.find(token => lower.startsWith(token));

    if (!match) {
      segments.push({ text: part, bold: false });
      return;
    }
    segments.push({ text: part.slice(0, match.length), bold: true });
    if (part.length > match.length) {
      segments.push({ text: part.slice(match.length), bold: false });
    }
  });

  // Merge neighbours of the same weight so the DOM stays minimal.
  return segments.reduce((acc, segment) => {
    const previous = acc[acc.length - 1];
    if (previous && previous.bold === segment.bold) {
      previous.text += segment.text;
      return acc;
    }
    return [...acc, { ...segment }];
  }, []);
};

export const optionDomId = (listboxId, index) => `${listboxId}-option-${index}`;

/**
 * ARIA wiring for the input that owns this listbox. Spread onto the <input>.
 *
 * The input lives in the parent (KeywordInput / CustomSearchBox), so the combobox attributes
 * cannot be set here — but they have to stay consistent with what this component renders,
 * hence keeping them in the same file.
 */
export const getComboboxInputProps = ({ isOpen, listboxId, highlightedIndex }) => ({
  role: 'combobox',
  'aria-expanded': !!isOpen,
  'aria-controls': listboxId,
  'aria-autocomplete': 'list',
  'aria-haspopup': 'listbox',
  ...(isOpen && highlightedIndex >= 0
    ? { 'aria-activedescendant': optionDomId(listboxId, highlightedIndex) }
    : {}),
});

/**
 * Keyboard semantics, shared by both search surfaces so they cannot drift apart.
 *
 * Returns true when the event was handled, so the caller knows to skip its own handling —
 * notably Enter, which otherwise commits a search for the half-typed text instead of the
 * suggestion the customer has highlighted.
 */
export const handleSuggestionKeyDown = (event, { isOpen, highlighted, moveHighlight, onSelect, onClose }) => {
  if (!isOpen) return false;

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      moveHighlight(1);
      return true;
    case 'ArrowUp':
      event.preventDefault();
      moveHighlight(-1);
      return true;
    case 'Escape':
      event.preventDefault();
      // Closes the list but deliberately keeps what the customer typed.
      onClose();
      return true;
    case 'Tab':
      onClose();
      return false;
    case 'Enter':
      if (!highlighted) return false;
      event.preventDefault();
      onSelect(highlighted.value);
      return true;
    default:
      return false;
  }
};

const SuggestionRow = props => {
  const { id, label, query, isHighlighted, onSelect, onHighlight } = props;

  return (
    <li
      id={id}
      role="option"
      aria-selected={isHighlighted}
      className={classNames(css.row, { [css.highlighted]: isHighlighted })}
      // mousedown rather than click, with preventDefault: the input's blur fires before click
      // would, which closed the dropdown and swallowed the tap on mobile. Keeping focus also
      // avoids the keyboard collapsing mid-selection.
      onMouseDown={event => {
        event.preventDefault();
        onSelect(label);
      }}
      onMouseEnter={onHighlight}
    >
      <IconCollection name="search" width="16px" height="16px" className={css.rowIcon} />
      <span className={css.rowLabel}>
        {highlightSegments(label, query).map((segment, index) =>
          segment.bold ? (
            <strong key={index} className={css.match}>
              {segment.text}
            </strong>
          ) : (
            <span key={index}>{segment.text}</span>
          )
        )}
      </span>
    </li>
  );
};

const SearchSuggestions = props => {
  const intl = useIntl();
  const {
    suggestions = [],
    query,
    isOpen,
    highlightedIndex = -1,
    onSelect,
    onHighlight,
    listboxId = 'search-suggestions',
    showHeading = false,
    rootClassName,
    className,
  } = props;

  const renderable = suggestions.filter(isRenderableSuggestion);

  if (!isOpen || !renderable.length) return null;

  const trimmedQuery = String(query == null ? '' : query).trim();
  const seeAllIndex = renderable.length;
  const isSeeAllHighlighted = highlightedIndex === seeAllIndex;

  return (
    <div className={classNames(rootClassName || css.root, className)}>
      {showHeading ? (
        <div className={css.heading} aria-hidden="true">
          <FormattedMessage id="SearchSuggestions.heading" />
        </div>
      ) : null}

      {/* Named even when the visible heading is hidden, so the list is announced as
          something rather than a bare "listbox". */}
      <ul
        className={css.list}
        id={listboxId}
        role="listbox"
        aria-label={intl.formatMessage({ id: 'SearchSuggestions.heading' })}
      >
        {renderable.map((suggestion, index) => (
          <SuggestionRow
            key={suggestion.value}
            id={optionDomId(listboxId, index)}
            label={suggestion.value}
            query={trimmedQuery}
            isHighlighted={highlightedIndex === index}
            onSelect={onSelect}
            onHighlight={() => onHighlight(index)}
          />
        ))}

        <li
          id={optionDomId(listboxId, seeAllIndex)}
          role="option"
          aria-selected={isSeeAllHighlighted}
          className={classNames(css.row, css.seeAll, { [css.highlighted]: isSeeAllHighlighted })}
          onMouseDown={event => {
            event.preventDefault();
            onSelect(trimmedQuery);
          }}
          onMouseEnter={() => onHighlight(seeAllIndex)}
        >
          <IconCollection name="search" width="16px" height="16px" className={css.rowIcon} />
          <span className={css.rowLabel}>
            <FormattedMessage
              id="SearchSuggestions.seeAllResults"
              values={{
                query: <strong className={css.match}>{trimmedQuery}</strong>,
              }}
            />
          </span>
        </li>
      </ul>
    </div>
  );
};

SearchSuggestions.propTypes = {
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string.isRequired, count: PropTypes.number })
  ),
  query: PropTypes.string,
  isOpen: PropTypes.bool,
  highlightedIndex: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onHighlight: PropTypes.func.isRequired,
  listboxId: PropTypes.string,
  showHeading: PropTypes.bool,
  rootClassName: PropTypes.string,
  className: PropTypes.string,
};

export default SearchSuggestions;
